import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import styled from "styled-components";
import { observer } from "mobx-react-lite";
import { fundraiserStore } from "../stores/FundraiserStore";
import { CallToAction } from "./CallToAction";
import { FundraiserCard } from "./FundraiserCard";
import {
  shouldShowExpirationBadge,
  getExpirationBadgeText,
} from "../utils/badgesUtils";
import { DonationCapture } from "./donation-capture/DonationCapture";
import { FundraiserEmbedViewType, FundRaiserStatus } from "../enums";
import { firstValueFrom } from "rxjs";
import { FundraiserService } from "../services";
import {
  CreateFundraiserModel,
  FundraiserDonationButtonColors,
} from "../models";
import { Loader } from "@mindmemobile/mm-shared-global";

const ContentContainer = styled.div`
  padding: 24px 40px 40px;
  display: grid;
  grid-template-columns: 1fr 0.8fr;
  gap: 40px;
  flex: 1;
  overflow-y: auto;
  min-height: 0;
`;

// Simple Donate Button Component
const SimpleDonateButton = styled.button<{ style?: React.CSSProperties }>`
  padding: 16px 46px;
  border: none;
  border-radius: 12px;
  font-size: 18px;
  font-weight: 700;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    transform: translateY(-2px);
  }

  &:active {
    transform: translateY(0);
  }

  &:disabled {
    background: #9ca3af;
    cursor: not-allowed;
    transform: none;
    box-shadow: none;

    &:hover {
      transform: none;
      box-shadow: none;
    }
  }
`;

// Responsive Donation Form Container for narrow preview
const ResponsiveDonationContainer = styled.div`
  width: 100%;
  max-width: 400px;

  .donation-form-container {
    padding: 24px;
  }

  /* Ensure comment label doesn't wrap */
  .comment-label-container {
    display: flex;
    align-items: center;
    flex-wrap: nowrap;
    gap: 8px;
  }

  .comment-label-text {
    white-space: nowrap;
    flex-shrink: 0;
  }

  .emoji-container {
    display: flex;
    align-items: center;
    gap: 8px;
    flex-shrink: 0;
  }
`;

interface EmbedFundraiserViewProps {
  type: FundraiserEmbedViewType;
  fundraiserId: string;
  promoterId: string;
}

export const EmbedFundraiserView: React.FC<EmbedFundraiserViewProps> = observer(
  ({ type, promoterId, fundraiserId }) => {
    const [showLoader, setShowLoader] = useState(true);

    const builderData = fundraiserStore.builderData;
    const customColors = builderData?.customColors || {
      primary: "#10b981",
      background: "#ffffff",
    };
    const donationButtonType = builderData?.donationButtonType || "gradient";
    const donationButtonColors =
      builderData?.donationButtonColors || new FundraiserDonationButtonColors();

    //THIS IS A PATCH TO ACCESS COUNTRY DATA IN FORM.
    const USCountry = fundraiserStore.countryStore.getUSCountry();
    useEffect(() => {
      fetchFundraiserData();
    }, [promoterId, fundraiserId]);

    const fetchFundraiserData = async () => {
      try {
        setShowLoader(true);
        const response = await firstValueFrom(
          FundraiserService.getFundraiserData(fundraiserId, promoterId || "")
        );
        if (response.result) {
          const data = CreateFundraiserModel.deserializeAPIModel(
            response.result
          );
          fundraiserStore.setFundraiserDetails(data);
          fundraiserStore.setBuilderData(data);
          fundraiserStore.initializeBuilder(data);
          setShowLoader(false);
        }
      } catch (error) {
        console.error("Failed to load fundraisers", error);
      }
    };

    const getDonationButtonStyles = () => {
      const background =
        donationButtonType === "solid"
          ? donationButtonColors.primary
          : `linear-gradient(135deg, ${donationButtonColors.primary}, ${donationButtonColors.secondary})`;

      return {
        background,
        color: donationButtonColors.text.rgba || "#ffffff",
        boxShadow: `0 4px 16px ${donationButtonColors.primary.rgba}30`,
      } as React.CSSProperties;
    };

    const getButtonText = () => {
      return "Donate";
    };

    const isButtonDisabled = () => {
      return false;
    };

    const openFundRaiserInNewTab = () => {
      const fundraiserUrl = fundraiserStore.getFundraiserPublicLinkFromModel(
        fundraiserStore.selectedFundraiser,
        promoterId
      );
      window.open(fundraiserUrl, "_blank");
    };

    const renderPreview = () => {
      if (showLoader) {
        return <>{new Loader(true).loaderComponent}</>;
      }
      const selectedFundraiser = fundraiserStore.selectedFundraiser;
      if (!selectedFundraiser) return null;

      switch (type) {
        case FundraiserEmbedViewType.Card:
          return (
            <FundraiserCard
              fundraiser={selectedFundraiser}
              isEmbedPreview={false}
              onClick={() => openFundRaiserInNewTab()}
            />
          );

        case FundraiserEmbedViewType.CTA:
          return (
            <div style={{ width: "100%", maxWidth: "380px" }}>
              <CallToAction
                customColors={customColors}
                isPreview={false}
                onDonateClick={() => openFundRaiserInNewTab()}
              />
            </div>
          );

        case FundraiserEmbedViewType.Button:
          return (
            <SimpleDonateButton
              disabled={isButtonDisabled()}
              style={getDonationButtonStyles()}
              onClick={() => openFundRaiserInNewTab()}
            >
              {getButtonText()}
            </SimpleDonateButton>
          );

        case FundraiserEmbedViewType.Form:
          return (
            <DonationCapture
              promoterId={promoterId}
              isVisible={true}
              onClose={() => {}}
              isEmbedded={true}
              shortUniqueId={
                fundraiserStore.selectedFundraiser?.shortUniqueId || ""
              }
            />
          );

        default:
          return null;
      }
    };

    return createPortal(
      <ContentContainer>{renderPreview()}</ContentContainer>,
      document.body
    );
  }
);
